import React from 'react';
import I18n from 'i18n-js';
import PayoutMethodCountryCodeInput from './payout_method_country_code_input';
import CurrencyInput from './payout_method_currency_input';
import BankDepositConfirmCheckbox from './payout_method_currency_warnings';
import CanadianRoutingNumberFields from './canadian_routing_number_fields';
import { PaymentProvider } from './payment_provider';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { Result as RoutingNumberResult, Success as RoutingNumberSuccess } from '../lib/verify_bank_routing_number';

const ACCOUNT_INPUT_LENGTH = 17;

const currencyCodes = ['CAD', 'USD'];

export interface Props {
  countryCodes: string[];
  inputNamePrefix: string;
  onCountryChange(countryCode: string): void;
  onCurrencyChange(currencyCode: string): void;
  payoutCurrency: string;
  shopCurrency: string;
  verifyBankRoutingNumber({ transitNumber, institutionNumber, countryCode }): Promise<RoutingNumberResult>;
}

interface State {
  bankName: string;
}

export default class CanadianBankFields extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.verifyRoutingNumber = this.verifyRoutingNumber.bind(this);

    this.state = {
      bankName: '',
    };
  }

  async verifyRoutingNumber(transitNumber: string, institutionNumber: string) {
    const result = await this.props.verifyBankRoutingNumber({
      transitNumber,
      institutionNumber,
      countryCode: 'CA',
    });

    this.setState({
      bankName: isRoutingNumberValid(result) ? result.name : '',
    });
  }

  displayValidBankInfo() {
    if (this.state.bankName === '') { return null; }
    return (
      <div>
        <hr />
        <p className="mb-0">
          <b>{this.state.bankName}</b>
        </p>
        <hr className="mb-1" />
      </div>
    );
  }

  hasValidSelectedPayoutCurrency() {
    return !!currencyCodes.find(currencyCode => currencyCode === this.props.payoutCurrency);
  }

  currencyForInput() {
    if (this.hasValidSelectedPayoutCurrency()) {
      return this.props.payoutCurrency;
    }

    return 'CAD';
  }

  inputName(methodName) {
    return `${this.props.inputNamePrefix}[${methodName}]`;
  }

  render() {
    return (
      <div>
        <div className="g-container mb-1">
          <div className="g-col-6 g-col-mobile-12">
            <label>
              {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.nameOnAccount')}
            </label>
            <input
              className="string required wide mb-0"
              autoFocus
              type="text"
              name={this.inputName('name_on_account')}
            />
          </div>
        </div>

        <div className="mb-1">
          <div className="g-container">
            <PayoutMethodCountryCodeInput
              countryCode="CA"
              countryCodes={this.props.countryCodes}
              inputName={this.inputName('country_code')}
              onCountryChange={this.props.onCountryChange}
            />
            <CurrencyInput
              currencyCode={this.currencyForInput()}
              currencyCodes={currencyCodes}
              inputName={this.inputName('currency')}
              onCurrencyChange={currencyCode => this.props.onCurrencyChange(currencyCode)}
            />
          </div>

          <BankDepositConfirmCheckbox
            inputName={this.inputName('bank_deposits_confirmed_at')}
            paymentProvider={PaymentProvider.ReverbPayments}
            payoutCurrencyCode={this.currencyForInput()}
            shopCountryCode="CA"
            shopCurrencyCode={this.props.shopCurrency}
          />
        </div>
        <div className="g-container">
          <div className="g-col-8 g-col-mobile-12">
            <CanadianRoutingNumberFields
              verifyRoutingNumber={this.verifyRoutingNumber}
              inputNamePrefix={this.props.inputNamePrefix}
            />
          </div>

          <div className="g-col-4 g-col-mobile-12">
            <label className="string required control-label">
              <SanitizedRender
                allowedTags={['span']}
                html={I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.canada.accountNumber', {
                  hint_open: '<span class="hint">',
                  hint_close: '</span>',
                })}
              />
            </label>
            <input
              className="string required wide"
              maxLength={ACCOUNT_INPUT_LENGTH}
              type="text"
              name={this.inputName('bank_account_number')}
            />
          </div>
        </div>
        {this.displayValidBankInfo()}
      </div>
    );
  }
}

function isRoutingNumberValid(result: RoutingNumberResult): result is RoutingNumberSuccess {
  return (result as RoutingNumberSuccess).routing_number !== undefined;
}
